import { FC, useState } from 'react'
import { Create, useNotify, usePermissions, useRedirect, useTranslate } from 'react-admin'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { HeaderTitle } from '../../components'
import { pageSx } from '../../components/ui'
import { CERTIFICATE_ORGANIZATION_SWITCH_SCOPE } from '../../config'
import useSwitchOrganization from '../../hooks/useSwitchOrganization'
import type { IPermissions, OrganizationRecord } from '../../types/apiTypes'
import OrganizationForm, { cleanApps } from './OrganizationForm'

const OrganizationsCreate: FC = () => {
  const notify = useNotify()
  const redirect = useRedirect()
  const translate = useTranslate()
  const { permissions } = usePermissions() as IPermissions
  // The API only honours the organization header with the switch scope.
  const canSwitch = !!permissions?.scopes?.includes(CERTIFICATE_ORGANIZATION_SWITCH_SCOPE)
  const switchOrganization = useSwitchOrganization()
  const [created, setCreated] = useState<OrganizationRecord | null>(null)

  const close = () => {
    setCreated(null)
    redirect('list', 'organization')
  }

  return (
    <>
      <Create
        component="div"
        actions={false}
        sx={pageSx}
        title={<HeaderTitle i18n="resources.organization.create" />}
        // Only these fields are accepted by the API.
        transform={(data: Partial<OrganizationRecord>) => ({
          name: data.name,
          organizationId: data.organizationId,
          apps: cleanApps(data.apps, false),
        })}
        mutationOptions={{
          onSuccess: (data: OrganizationRecord) => {
            notify('resources.organization.created', { type: 'success' })
            setCreated(data)
          },
        }}
      >
        <OrganizationForm />
      </Create>
      <Dialog open={!!created} onClose={close}>
        <DialogTitle>{translate('resources.organization.created')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{created?.name}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{translate('resources.organization.close')}</Button>
          {canSwitch && (
            <Button
              variant="contained"
              onClick={() => {
                if (created) switchOrganization(created.organizationId, created.name)
                close()
              }}
            >
              {translate('resources.organization.switchTo')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default OrganizationsCreate
